// src/utils/validationMessages.ts
// Centralized validation message dictionary for all forms

/**
 * Validation message types for type-safe message keys
 */
export type ValidationMessageKey =
  | 'required'
  | 'email'
  | 'emailInvalid'
  | 'phone'
  | 'phoneInvalid'
  | 'select'
  | 'date'
  | 'dateInvalid'
  | 'dateRange'
  | 'positiveNumber'
  | 'minLength'
  | 'maxLength'
  | 'conditionalRequired';

/**
 * Field-specific validation messages
 */
export interface FieldValidationMessages {
  // Login form
  username: string;
  password: string;
  // Client form fields
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  vetOther: string;
  notes: string;
  notesWhenWarning: string;

  // Pet form fields
  petName: string;
  petType: string;
  petSize: string;
  petBreed: string;
  gender: string;
  petNotes: string;
  petNotesWhenWarning: string;

  // Booking form fields
  petSelection: string;
  petsRequired: string;
  checkInDate: string;
  checkOutDate: string;
  checkOutAfterCheckIn: string;
  dailyRate: string;
  validDailyRate: string;

  // Surcharge form fields
  surchargeReason: string;
  surchargeAmount: string;
  surchargeAmountInvalid: string;
}

/**
 * Generic validation messages that can be reused with field names
 */
export const VALIDATION_MESSAGES = {
  // Generic required messages
  required: (fieldName: string) => `${fieldName} is required`,

  // Email validation
  email: 'Email is required',
  emailInvalid: 'Email is invalid',

  // Phone validation
  phone: 'Phone number is required',
  phoneInvalid: 'Phone number is invalid',

  // Selection validation
  select: (fieldName: string) => `${fieldName} selection is required`,

  // Date validation
  date: (fieldName: string) => `${fieldName} is required`,
  dateInvalid: (fieldName: string) => `${fieldName} is invalid`,
  dateRange: (startField: string, endField: string) =>
    `${endField} must be after ${startField}`,

  // Number validation
  positiveNumber: (fieldName: string) =>
    `Valid ${fieldName.toLowerCase()} is required`,

  // Length validation
  minLength: (fieldName: string, length: number) =>
    `${fieldName} must be at least ${length} characters`,
  maxLength: (fieldName: string, length: number) =>
    `${fieldName} must be less than ${length} characters`,

  // Conditional validation
  conditionalRequired: (fieldName: string, condition: string) =>
    `${fieldName} is required when ${condition}`,
};

/**
 * Specific field validation messages for direct use
 */
export const FIELD_MESSAGES: FieldValidationMessages = {
  // Login form
  username: 'Username is required',
  password: 'Password is required',

  // Client form
  firstName: 'First name is required',
  lastName: 'Last name is required',
  email: 'Email is required',
  phone: 'Phone number is required',
  vetOther: 'Other vet name is required when "Other..." is selected',
  notes: 'Notes are required when Show Warning is enabled',
  notesWhenWarning: 'Notes are required when Show Warning is enabled',

  // Pet form
  petName: 'Pet name is required',
  petType: 'Type is required',
  petSize: 'Size is required',
  petBreed: 'Breed is required',
  gender: 'Gender is required',
  petNotes: 'Notes are required when Show Warning is enabled',
  petNotesWhenWarning: 'Notes are required when Show Warning is enabled',

  // Booking form
  petSelection: 'Pet selection is required',
  petsRequired: 'Please include at least 1 pet',
  checkInDate: 'Check-in date is required',
  checkOutDate: 'Check-out date is required',
  checkOutAfterCheckIn: 'Invalid date-range',
  dailyRate: 'Daily rate is required',
  validDailyRate: 'Valid daily rate is required',

  // Surcharge form
  surchargeReason: 'Reason is required',
  surchargeAmount: 'Amount is required',
  surchargeAmountInvalid: 'Amount is required',
};

export const API_ERROR_MESSAGES = {
  // Client operations
  LOAD_CLIENTS: 'Failed to load clients. Please try again.',
  LOAD_CLIENT: 'Failed to load client details. Please try again.',
  CREATE_CLIENT: 'Failed to create client. Please try again.',
  UPDATE_CLIENT: 'Failed to update client. Please try again.',
  DELETE_CLIENT: 'Failed to delete client. Please try again.',

  // Pet operations
  LOAD_PETS: 'Failed to load pets. Please try again.',
  LOAD_PET: 'Failed to load pet details. Please try again.',
  CREATE_PET: 'Failed to create pet. Please try again.',
  UPDATE_PET: 'Failed to update pet. Please try again.',
  DELETE_PET: 'Failed to delete pet. Please try again.',

  // Booking operations
  LOAD_BOOKINGS: 'Failed to load bookings. Please try again.',
  CREATE_BOOKING: 'Failed to create booking. Please try again.',
  UPDATE_BOOKING: 'Failed to update booking. Please try again.',
  DELETE_BOOKING: 'Failed to delete booking. Please try again.',

  // Reference data operations
  LOAD_REFERENCE_DATA: 'Failed to load reference data. Please try again.',
  LOAD_VETS: 'Failed to load veterinarians. Please try again.',
  LOAD_PET_TYPES: 'Failed to load pet types. Please try again.',
  LOAD_PET_SIZES: 'Failed to load pet sizes. Please try again.',
  LOAD_PET_BREEDS: 'Failed to load pet breeds. Please try again.',
  LOAD_PET_COLOURS: 'Failed to load pet colours. Please try again.',

  // Kennel operations
  LOAD_KENNELS: 'Failed to load kennels. Please try again.',
} as const;

// Type for API error message keys
export type ApiErrorMessageKey = keyof typeof API_ERROR_MESSAGES;

/**
 * Helper function to get a validation message
 * @param key - The message key from FIELD_MESSAGES
 * @returns The validation message string
 */
export const getMessage = (key: keyof FieldValidationMessages): string => {
  return FIELD_MESSAGES[key];
};

/**
 * Helper function to create a custom required message
 * @param fieldName - The name of the field
 * @returns A formatted required message
 */
export const getRequiredMessage = (fieldName: string): string => {
  return VALIDATION_MESSAGES.required(fieldName);
};

/**
 * Helper function to create a custom selection required message
 * @param fieldName - The name of the field
 * @returns A formatted selection required message
 */
export const getSelectMessage = (fieldName: string): string => {
  return VALIDATION_MESSAGES.select(fieldName);
};

/**
 * Helper function to create a date range validation message
 * @param startField - The start date field name
 * @param endField - The end date field name
 * @returns A formatted date range message
 */
export const getDateRangeMessage = (
  startField: string,
  endField: string
): string => {
  return VALIDATION_MESSAGES.dateRange(startField, endField);
};

/**
 * Helper function to create a conditional required message
 * @param fieldName - The name of the field
 * @param condition - The condition description
 * @returns A formatted conditional required message
 */
export const getConditionalRequiredMessage = (
  fieldName: string,
  condition: string
): string => {
  return VALIDATION_MESSAGES.conditionalRequired(fieldName, condition);
};
